import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { Shield, Eye, EyeOff, ArrowLeft, Lock } from 'lucide-react';

// Pre-computed particles with amber/gold palette
const PARTICLES = Array.from({ length: 22 }, (_, i) => ({
  id: i,
  size: 2 + (i % 3),
  left: (i * 4.5 + 11) % 100,
  delay: (i * 0.6) % 13,
  duration: 17 + (i % 11),
  drift: ((i % 2 === 0) ? 1 : -1) * (14 + (i % 36)),
}));

function AnimatedBg() {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none select-none">
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(135deg, #080810 0%, #110B00 50%, #0C0700 100%)' }}
      />
      {/* Amber orbs */}
      <div
        className="absolute -top-48 -right-48 w-[650px] h-[650px] rounded-full animate-blob"
        style={{ background: 'radial-gradient(circle, rgba(217,119,6,0.14) 0%, transparent 70%)' }}
      />
      <div
        className="absolute -bottom-48 -left-48 w-[550px] h-[550px] rounded-full animate-blob animation-delay-2000"
        style={{ background: 'radial-gradient(circle, rgba(180,83,9,0.12) 0%, transparent 70%)' }}
      />
      <div
        className="absolute top-1/3 left-1/3 w-[400px] h-[400px] rounded-full animate-blob animation-delay-4000"
        style={{ background: 'radial-gradient(circle, rgba(146,64,14,0.10) 0%, transparent 70%)' }}
      />

      {/* Dot grid */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.07) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          opacity: 0.4,
        }}
      />

      {/* Amber particles */}
      {PARTICLES.map(p => (
        <div
          key={p.id}
          className="absolute rounded-full"
          style={{
            width: p.size,
            height: p.size,
            left: `${p.left}%`,
            bottom: '-8px',
            background: 'rgba(251,191,36,0.45)',
            animation: `particle-float ${p.duration}s ${p.delay}s infinite linear`,
            '--particle-drift': `${p.drift}px`,
          }}
        />
      ))}
    </div>
  );
}

export default function SuperAdminLogin() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Already logged in as super admin → go straight to dashboard
  useEffect(() => {
    if (isAuthenticated && user?.role === 'super_admin') {
      navigate('/super-admin/dashboard', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Email is sent as the username field — backend accepts both email and username
      await login('SUPERADMIN', form.email, form.password);
      navigate('/super-admin/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Invalid credentials. Access denied.');
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    'w-full px-4 py-3.5 rounded-xl text-sm text-white placeholder-white/20 outline-none transition-all duration-200 ' +
    'bg-white/[0.06] border border-amber-500/15 focus:border-amber-500/40 focus:bg-white/[0.09] ' +
    'focus:ring-2 focus:ring-amber-500/15';

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-14 relative">
      <AnimatedBg />

      <div className="relative z-10 w-full max-w-sm">
        {/* Back link */}
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-300 transition-colors text-sm group mb-10"
          >
            <ArrowLeft size={15} className="group-hover:-translate-x-1 transition-transform duration-150" />
            Back to main login
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.05, type: 'spring', stiffness: 200 }}
          className="text-center mb-10"
        >
          <motion.div
            animate={{ y: [0, -5, 0] }}
            transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
            className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-5 shadow-2xl"
            style={{
              background: 'linear-gradient(135deg, #D97706, #92400E)',
              boxShadow: '0 20px 60px rgba(217,119,6,0.45)',
            }}
          >
            <Shield size={32} className="text-white" />
          </motion.div>

          <h1 className="text-3xl font-extrabold text-white tracking-tight">
            Administrator Portal
          </h1>
          <p className="text-gray-500 text-sm mt-2.5 leading-relaxed">
            Restricted access — authorised personnel only
          </p>
        </motion.div>

        {/* Glass card */}
        <motion.div
          initial={{ opacity: 0, y: 28, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.12, type: 'spring', stiffness: 220, damping: 26 }}
          className="rounded-2xl border p-8"
          style={{
            background: 'rgba(255,255,255,0.04)',
            backdropFilter: 'blur(28px)',
            borderColor: 'rgba(217,119,6,0.2)',
          }}
        >
          {/* Access badge */}
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] mb-7"
            style={{ background: 'rgba(217,119,6,0.15)', color: '#FCD34D', border: '1px solid rgba(217,119,6,0.3)' }}
          >
            <Lock size={10} />
            Secure Admin Access
          </div>

          <h2 className="text-[22px] font-bold text-white mb-1">Sign in</h2>
          <p className="text-gray-400 text-sm mb-8">Use your admin email and password</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-[0.18em] mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                placeholder="admin@examgenius.in"
                className={inputCls}
                autoComplete="email"
                required
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-[0.18em] mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))}
                  placeholder="••••••••••••"
                  className={`${inputCls} pr-12`}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPass(p => !p)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-200 transition-colors"
                >
                  {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <motion.button
              type="submit"
              disabled={loading}
              whileHover={{
                scale: 1.025,
                boxShadow: '0 12px 40px rgba(217,119,6,0.5)',
              }}
              whileTap={{ scale: 0.97 }}
              className="w-full py-[15px] rounded-xl text-white font-bold text-sm mt-2 relative overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg, #D97706, #92400E)' }}
            >
              <span
                className="absolute inset-0"
                style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)' }}
              />
              <span className="relative z-10 flex items-center justify-center gap-2">
                <Shield size={15} />
                {loading ? 'Verifying access…' : 'Access Admin Portal →'}
              </span>
            </motion.button>
          </form>
        </motion.div>

        {/* Default credentials hint (dev only) */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="mt-6 p-3.5 rounded-xl text-center"
          style={{ background: 'rgba(217,119,6,0.06)', border: '1px solid rgba(217,119,6,0.15)' }}
        >
          <p className="text-[11px] text-amber-600/70 leading-relaxed">
            Default email: <span className="text-amber-500 font-mono">admin@examgenius.in</span>
            <br />
            Password set via <span className="font-mono">SUPER_ADMIN_PASSWORD</span> in <span className="font-mono">.env</span>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
